import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, iif, of } from 'rxjs';
import { map, mergeMap, withLatestFrom, catchError } from 'rxjs/operators';

/* notification service */
import { NotificationService } from 'src/app/features/shared/services/notification-service/notification.service';

/* jsonata object class */
import { JSONAtaObject } from 'src/app/features/shared/helper-classes';

/* lodash lib */
import * as lodash from 'lodash';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* navigate action */
import { Navigate } from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* Workflow Management API Service */
import { WorkflowManagementControllerService } from 'src/app/features/shared/sdk/services/workflow-management-controller.service';

/* pharmacy-data-store actions */
import * as PharmacyDataStoreActions from 'src/app/features/data-stores/pharmacy-data-store/state/pharmacy-data-store.actions';

/* api services */
import { PharmacyControllerService } from 'src/app/features/shared/sdk/services';

@Injectable()
export class PharmacyDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private notificationService: NotificationService,
    private workflowManagementApi: WorkflowManagementControllerService,
    private pharmacyApiService: PharmacyControllerService,
  ) { 
    // prettier-ignore
    // #region customCode: data-store-effects__constructor__start
    
    // #endregion customCode: data-store-effects__constructor__start

    // prettier-ignore
    // #region customCode: data-store-effects__constructor__end
    
    // #endregion customCode: data-store-effects__constructor__end
  }

  getPharmacies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PharmacyDataStoreActions.GetPharmacies),
      mergeMap((action) => {
        return this.pharmacyApiService.find().pipe(
          mergeMap((pharmacies) => {
            const payload: any = {
              pharmacies: pharmacies,
            };
            return [
              PharmacyDataStoreActions.GetPharmaciesSuccessful({ payload }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while fetching data.',
            );
            return [PharmacyDataStoreActions.GetPharmaciesFailure()];
          }),
        );
      }),
    ),
  );

  getPharmacyById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PharmacyDataStoreActions.GetPharmacyById),
      mergeMap((action) => {
        return this.pharmacyApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((pharmacy) => {
              const payload: any = {
                pharmacy: pharmacy,
              };
              return [
                PharmacyDataStoreActions.GetPharmacyByIdSuccessful({ payload }),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [PharmacyDataStoreActions.GetPharmacyByIdFailure()];
            }),
          );
      }),
    ),
  );

  createPharmacy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PharmacyDataStoreActions.CreatePharmacy),
      mergeMap((action) => {
        const params = {
          body: action.payload.pharmacy,
        };
        return this.pharmacyApiService.create(<any>params).pipe(
          mergeMap((pharmacy) => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been saved successfully.',
            );
            const payload: any = {
              pharmacy: pharmacy,
            };
            return [
              PharmacyDataStoreActions.CreatePharmacySuccessful({ payload }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while saving the data.',
            );
            return [PharmacyDataStoreActions.CreatePharmacyFailure()];
          }),
        );
      }),
    ),
  );

  updatePharmacy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PharmacyDataStoreActions.UpdatePharmacy),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: action.payload.data,
        };
        return this.pharmacyApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been updated successfully.',
            );
            return [PharmacyDataStoreActions.GetPharmacies()];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while updating the data.',
            );
            return [PharmacyDataStoreActions.UpdatePharmacyFailure()];
          }),
        );
      }),
    ),
  );

  deletePharmacy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PharmacyDataStoreActions.DeletePharmacy),
      mergeMap((action) => {
        const params = { id: action.payload.id };
        return this.pharmacyApiService.deleteById(params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Your data has been deleted successfully.',
            );
            const payload: any = {
              id: action.payload.id,
            };
            return [
              PharmacyDataStoreActions.DeletePharmacySuccessful({ payload }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while deleting the data.',
            );
            return [PharmacyDataStoreActions.DeletePharmacyFailure()];
          }),
        );
      }),
    ),
  );
}
